'use client'

import { useState, useCallback, useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { MarkdownEditor } from './markdown-editor'
import { MarkdownToolbar, type ToolbarAction } from './markdown-toolbar'
import { MarkdownPreview } from './markdown-preview'
import { getInsertionForAction } from '@/lib/markdown-formatting'
import type { EditorView } from '@codemirror/view'

export interface MarkdownInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  minRows?: number
  /**
   * Optional upper bound on editor height, expressed as a row count.
   * When set, the container gets a computed `maxHeight` of
   * `maxRows * ROW_HEIGHT_PX` px and the same value is forwarded to
   * `MarkdownEditor` so CodeMirror's internal `.cm-scroller` scroll engages.
   * Callers that omit `maxRows` are unaffected (editor grows unbounded).
   */
  maxRows?: number
  disabled?: boolean
  hideToolbar?: boolean
  maxLength?: number
  id?: string
  name?: string
  /** Focus the editor automatically when mounted */
  autoFocus?: boolean
  /**
   * Accessible name for the editor's contenteditable region, forwarded to
   * `MarkdownEditor` (and ultimately CodeMirror's `EditorView.contentAttributes`
   * as `aria-label`). Needed because `id` is placed on the outer wrapper
   * `<div>`, not on CodeMirror's editable region, so `<label htmlFor={id}>`
   * never programmatically associates with anything labelable.
   *
   * Callers that pair this with a visible `<label>` should keep this text in
   * sync with the label's visible text (WCAG 2.5.3 Label in Name) — there is
   * no single-source mechanism (e.g. `aria-labelledby`) wired through to
   * CodeMirror's content region.
   */
  ariaLabel?: string
}

// Approximate row height for min/max height calculation
const ROW_HEIGHT_PX = 24

// Combined height of the on-focus chrome that sits OUTSIDE the scroll
// region: toolbar (~49px: py-1.5 * 2 + h-9 buttons + border-b) +
// helper/char-count footer (~31px: py-1.5 * 2 + text-xs + border-t),
// about 80px. The scroller's forwarded maxHeight is reduced by this when
// focused so its reachable bottom clears the chrome instead of being
// clipped behind the footer.
// IMPORTANT: this is a fixed estimate of the toolbar + footer markup. If
// MarkdownToolbar or the footer's padding/border/typography changes, RE-MEASURE
// and update this constant (the unit tests assert the >= / < relationships but
// not the absolute value, so they will NOT catch under-reservation drift).
const CHROME_HEIGHT_PX = 80

// Baseline minHeight padding for the OUTER container (applies at rest, when the
// editor is blurred and no chrome is shown). Kept separate from
// CHROME_HEIGHT_PX so widening the focused-chrome estimate does not change every
// consumer's resting height. Unchanged from the original value.
const RESTING_MIN_PAD_PX = 40

// Blur check runs after this delay to allow toolbar clicks to register
const BLUR_CHECK_DELAY_MS = 150
// Preview toggle guard must be > BLUR_CHECK_DELAY_MS so the blur
// handler sees the flag before it's cleared
const PREVIEW_TOGGLE_GUARD_MS = 200

export function MarkdownInput({
  value,
  onChange,
  placeholder = '',
  minRows = 5,
  maxRows,
  disabled = false,
  hideToolbar = false,
  maxLength,
  id,
  name,
  autoFocus = false,
  ariaLabel,
}: MarkdownInputProps) {
  const [isFocused, setIsFocused] = useState(false)
  const [isPreview, setIsPreview] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const editorViewRef = useRef<EditorView | null>(null)
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )
  const isTogglingPreviewRef = useRef(false)
  const toggleTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )

  const minHeight = `${minRows * ROW_HEIGHT_PX}px`
  const maxHeight =
    maxRows !== undefined ? `${maxRows * ROW_HEIGHT_PX}px` : undefined
  // When focused, the on-focus chrome (toolbar + footer) consumes
  // CHROME_HEIGHT_PX of the container's pixel budget. Forward a reduced cap
  // to the scroller so it can reach its bottom without being clipped by
  // overflow-hidden. When blurred, chrome is absent so the full cap is correct.
  // When maxRows is absent (unbounded), both values stay undefined.
  const minHeightPx = minRows * ROW_HEIGHT_PX
  const scrollerMaxHeight =
    maxRows !== undefined && isFocused
      ? `${Math.max(minHeightPx, maxRows * ROW_HEIGHT_PX - CHROME_HEIGHT_PX)}px`
      : maxHeight

  const handleChange = useCallback(
    (newValue: string) => {
      onChange(newValue)
    },
    [onChange],
  )

  const handleToolbarAction = useCallback(
    (action: ToolbarAction) => {
      const view = editorViewRef.current
      if (view) {
        const { from, to } = view.state.selection.main
        const selectedText = view.state.sliceDoc(from, to)
        const isLineStart =
          from === 0 || view.state.sliceDoc(from - 1, from) === '\n'
        const { before, after, placeholder } = getInsertionForAction(
          action,
          selectedText,
          isLineStart,
        )
        const content = selectedText || placeholder
        const insert = `${before}${content}${after}`
        if (
          maxLength &&
          view.state.doc.length - (to - from) + insert.length > maxLength
        ) {
          return
        }
        view.dispatch({
          changes: { from, to, insert },
          selection: {
            anchor: from + before.length,
            head: from + before.length + content.length,
          },
        })
        view.focus()
        return
      }
      // Fallback: append to value if no editor view available
      const { before, after, placeholder } = getInsertionForAction(
        action,
        '',
        value.length === 0 || value.endsWith('\n'),
      )
      const newValue = `${value}${before}${placeholder}${after}`
      if (maxLength && newValue.length > maxLength) {
        return
      }
      onChange(newValue)
    },
    [value, onChange, maxLength],
  )

  const handleTogglePreview = useCallback(() => {
    // Issue #3647: Sync editor content to parent state before
    // toggling to preview. CodeMirror's internal document may
    // have content that hasn't been flushed to React state yet
    // (e.g., due to batched updates). Reading directly from the
    // EditorView ensures the preview always shows current text.
    if (!isPreview) {
      const view = editorViewRef.current
      if (view) {
        const editorContent = view.state.doc.toString()
        if (editorContent !== value) {
          onChange(editorContent)
        }
      }
    }

    // Set flag so blur handler ignores the blur caused by
    // unmounting the editor when entering preview mode
    isTogglingPreviewRef.current = true
    setIsPreview(prev => !prev)
    toggleTimeoutRef.current = setTimeout(() => {
      isTogglingPreviewRef.current = false
    }, PREVIEW_TOGGLE_GUARD_MS)
  }, [isPreview, value, onChange])

  const handleFocus = useCallback(() => {
    setIsFocused(true)
  }, [])

  const handleBlur = useCallback(() => {
    // Delay blur check to allow toolbar clicks to register
    blurTimeoutRef.current = setTimeout(() => {
      // Skip if we're toggling preview - the blur was caused by
      // unmounting the editor, not by the user clicking outside
      if (isTogglingPreviewRef.current) return
      if (
        containerRef.current &&
        !containerRef.current.contains(document.activeElement)
      ) {
        setIsFocused(false)
        setIsPreview(false)
      }
    }, BLUR_CHECK_DELAY_MS)
  }, [])

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current)
      if (toggleTimeoutRef.current) clearTimeout(toggleTimeoutRef.current)
    }
  }, [])

  // Auto-focus the editor when mounted
  useEffect(() => {
    if (!autoFocus) return
    const timer = setTimeout(() => {
      editorViewRef.current?.focus()
    }, 100)
    return () => clearTimeout(timer)
  }, [autoFocus])

  // Detect dark mode from document
  const [isDark, setIsDark] = useState(false)
  useEffect(() => {
    const html = document.documentElement
    const checkDark = () => {
      setIsDark(html.classList.contains('dark'))
    }
    checkDark()
    const observer = new MutationObserver(checkDark)
    observer.observe(html, {
      attributes: true,
      attributeFilter: ['class'],
    })
    return () => observer.disconnect()
  }, [])

  // Hidden input for form integration
  const hiddenInput = name ? (
    <input type="hidden" name={name} value={value} />
  ) : null

  const showToolbar = !hideToolbar && isFocused
  const showHelperText = isFocused

  return (
    <div
      ref={containerRef}
      id={id}
      className={cn(
        'rounded-lg border overflow-hidden transition-colors resize-y flex flex-col',
        'bg-surface-elevated',
        isFocused
          ? 'border-stroke-selected ring-2 ring-stroke-selected ring-offset-2 dark:ring-offset-slate-900'
          : 'border-stroke-light',
        disabled && 'opacity-50 cursor-not-allowed',
      )}
      style={{
        minHeight: `${minRows * ROW_HEIGHT_PX + RESTING_MIN_PAD_PX}px`,
        ...(maxHeight ? { maxHeight } : {}),
      }}
      onFocus={handleFocus}
      onBlur={handleBlur}
      data-testid="markdown-input"
    >
      {hiddenInput}

      {/* Toolbar - only visible when focused */}
      {showToolbar && (
        <MarkdownToolbar
          onAction={handleToolbarAction}
          isPreview={isPreview}
          onTogglePreview={handleTogglePreview}
          disabled={disabled}
        />
      )}

      {/* Editor or Preview - flex-1 to fill resizable container */}
      {/* Preview mode makes this wrapper a column flex container so
          MarkdownPreview (flex-1 min-h-0) is sized by the flex algorithm to
          the bounded space left under the container's maxHeight, and its
          overflow-y-auto engages. A percentage height (h-full) does NOT work
          here: max-height leaves this wrapper's height indefinite, so h-full
          computes to auto and the preview grows to its content (#13159). */}
      <div
        className={cn(
          'flex-1 min-h-0',
          isPreview && 'flex flex-col overflow-hidden',
        )}
      >
        {isPreview ? (
          <MarkdownPreview
            value={value}
            minHeight={maxHeight ? undefined : minHeight}
          />
        ) : (
          <MarkdownEditor
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            disabled={disabled}
            isDark={isDark}
            minHeight={minHeight}
            maxHeight={scrollerMaxHeight}
            onFocus={handleFocus}
            onBlur={handleBlur}
            editorViewRef={editorViewRef}
            maxLength={maxLength}
            ariaLabel={ariaLabel}
          />
        )}
      </div>

      {/* Helper text and character count - only visible when focused */}
      {showHelperText && (
        <div
          data-testid="markdown-input-footer"
          className={cn(
            'flex items-center justify-between',
            'px-3 py-1.5',
            'border-t border-stroke-regular',
            'text-xs text-text-secondary',
            'bg-surface-elevated',
          )}
        >
          <span>
            Markdown formatting supported.{' '}
            <a
              href="/markdown"
              target="_blank"
              rel="noopener noreferrer"
              className="text-status-blue-text hover:underline"
            >
              Learn more
              <span className="sr-only">
                {' '}
                about Markdown formatting (opens in a new tab)
              </span>
            </a>
          </span>
          {maxLength && (
            <span>
              {value.length}/{maxLength}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
